import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, Clock, Users } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Case Studies — Real Businesses Running on Autopilot
  Meta Desc: See how business owners across every industry use MISTR to reclaim their time, automate operations, and grow without hiring. Real results. Real numbers.
  Primary KW: AI business automation case studies
*/
export function CaseStudies() {
  const [activeIndustry, setActiveIndustry] = useState('All');
  const industries = ['All', 'Insurance', 'Marketing Agency', 'Construction', 'Professional Services'];
  const caseStudies = [{
    company: 'KRT Insurance',
    industry: 'Insurance',
    headline: '67% fewer hours managing projects',
    excerpt: 'How Kathy Torres automated her entire client intake and policy renewal process, saving her team hundreds of hours a month.',
    metrics: [{
      label: 'Time Saved',
      value: '67%',
      icon: <Clock size={16} data-id="element-1358" data-mp="8ejdbx25qqb3" />
    }, {
      label: 'Faster Onboarding',
      value: '3x',
      icon: <TrendingUp size={16} data-id="element-1359" data-mp="3hj6hjxhhjix" />
    }, {
      label: 'Additional Hires',
      value: '$0',
      icon: <Users size={16} data-id="element-1360" data-mp="dcuqagnr1gxj" />
    }]
  }, {
    company: 'Apex Marketing Co',
    industry: 'Marketing Agency',
    headline: 'Scaled to 40 clients without hiring',
    excerpt: 'Apex replaced three open account manager roles with MR, handling all client reporting and routine communication automatically.',
    metrics: [{
      label: 'Clients Managed',
      value: '40',
      icon: <Users size={16} data-id="element-1361" data-mp="w56oqsba7y2e" />
    }, {
      label: 'Active Streams',
      value: '12',
      icon: <TrendingUp size={16} data-id="element-1362" data-mp="1a3wo6elkq9tx" />
    }, {
      label: 'Less Admin',
      value: '90%',
      icon: <Clock size={16} data-id="element-1363" data-mp="152peg9zvf0km" />
    }]
  }, {
    company: 'BuildRight Construction',
    industry: 'Construction',
    headline: 'Zero missed follow-ups in 6 months',
    excerpt: "By connecting MR to their field team's SMS and email, BuildRight ensured every subcontractor and client update happened instantly.",
    metrics: [{
      label: 'Missed Follow-ups',
      value: '0',
      icon: <Users size={16} data-id="element-1364" data-mp="liezdj89dr82" />
    }, {
      label: 'On-time Delivery',
      value: '100%',
      icon: <TrendingUp size={16} data-id="element-1365" data-mp="qff51t174ryn" />
    }, {
      label: 'Hours/Week Saved',
      value: '4hrs',
      icon: <Clock size={16} data-id="element-1366" data-mp="vgl2u59pr5f1" />
    }]
  }];
  const filteredStudies = activeIndustry === 'All' ? caseStudies : caseStudies.filter(study => study.industry === activeIndustry);
  return <main className="pt-20" data-id="element-1367" data-mp="2x9iwu4f8841k">
      <section className="section-padding bg-dark-bg min-h-screen relative overflow-hidden" data-id="element-1368" data-mp="31889fusfxhk5">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1369" data-mp="2tq0nl5i4zxtp" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1370" data-mp="2owpawnqn8mtv" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-1371" data-mp="2fzot4zy2xmze" />

        <div className="inner-container relative z-10" data-id="element-1372" data-mp="33mwu55oh69vm">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1373" data-mp="33pj399r0kxfy">
            <span className="eyebrow" data-id="element-1374" data-mp="2ynjybvfmlsgw">Case Studies</span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] mb-6" data-id="element-1375" data-mp="1s7zuz2yku4aj">
              Real Businesses.
              <br data-id="element-1376" data-mp="1n8542zesxeph" />
              <span className="text-gradient" data-id="element-1377" data-mp="1n7lldghbumae">Running Themselves.</span>
            </h1>
            <p className="text-lg text-steel" data-id="element-1378" data-mp="3b52v656wi7ao">
              See how business owners across every industry use MISTR to reclaim
              their time and grow without hiring.
            </p>
          </div>

          {/* Industry Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-16" data-id="element-1379" data-mp="323d5p4wkxifj">
            {industries.map(industry => <button key={industry} onClick={() => setActiveIndustry(industry)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${activeIndustry === industry ? 'bg-white text-dark-bg shadow-[0_0_15px_rgba(255,255,255,0.3)]' : 'bg-white/5 text-steel hover:text-white border border-white/10 backdrop-blur-sm'}`} data-id="element-1380" data-mp="3twlyye8r1pdr">
                {industry}
              </button>)}
          </div>

          {/* Case Studies Grid */}
          <div className="grid lg:grid-cols-2 gap-8" data-id="element-1381" data-mp="2efvlbufn9mml">
            {filteredStudies.map((study, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden group hover:border-teal/40 hover:shadow-[0_0_30px_rgba(7,133,183,0.15)] transition-all" data-id="element-1382" data-mp="25xtiqa1mt2cc" data-mpi={i}>
                <div className="p-8" data-id="element-1383" data-mp="1wyokz5xw9d18">
                  <div className="flex items-center justify-between mb-6" data-id="element-1384" data-mp="1wzpwtcmqi3k0">
                    <h3 className="font-poppins font-bold text-xl" data-id="element-1385" data-mp="1s5vkiu9gkquy">
                      {study.company}
                    </h3>
                    <span className="bg-white/10 text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full" data-id="element-1386" data-mp="ziivmdfts6yh">
                      {study.industry}
                    </span>
                  </div>

                  <h4 className="text-2xl font-bold text-teal mb-4 leading-tight" data-id="element-1387" data-mp="34jbry2l9nnab">
                    "{study.headline}"
                  </h4>
                  <p className="text-steel mb-8" data-id="element-1388" data-mp="39bddyg88ksdj">{study.excerpt}</p>

                  <div className="grid grid-cols-3 gap-4 mb-8 pt-6 border-t border-white/10" data-id="element-1389" data-mp="3db3zo5iwoib7">
                    {study.metrics.map((metric, j) => <div key={j} data-id="element-1390" data-mp="2go95u2dujxxj" data-mpi={j}>
                        <div className="text-2xl md:text-3xl font-poppins font-black text-white mb-1" data-id="element-1391" data-mp="2lk7orageonqk">
                          {metric.value}
                        </div>
                        <div className="text-xs text-steel flex items-center gap-1" data-id="element-1392" data-mp="2ulnv9743o5pi">
                          <span className="text-purple" data-id="element-1393" data-mp="2ujb9eiv81b76">{metric.icon}</span>{' '}
                          {metric.label}
                        </div>
                      </div>)}
                  </div>

                  <button className="text-teal font-semibold flex items-center gap-2 group-hover:gap-3 transition-all" data-id="element-1394" data-mp="jzwu6a7j8sks">
                    Read Case Study <ArrowRight size={16} data-id="element-1395" data-mp="f1lng8cjay6s" />
                  </button>
                </div>
              </motion.div>)}
          </div>

          <div className="mt-20 text-center bg-gradient-to-br from-purple/10 to-teal/10 backdrop-blur-md border border-white/10 rounded-2xl p-12 shadow-[0_0_40px_rgba(124,58,237,0.1)] relative overflow-hidden" data-id="element-1396" data-mp="67yroi8btuxl">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full blur-[80px] pointer-events-none" data-id="element-1397" data-mp="3senlqmo3qbok" />
            <h2 className="font-poppins font-bold text-3xl mb-4 relative z-10" data-id="element-1398" data-mp="3ni6p142u6ozv">
              Ready to be our next success story?
            </h2>
            <p className="text-steel mb-8 max-w-xl mx-auto relative z-10" data-id="element-1399" data-mp="1lp0n7rg25b90">
              Stop managing tasks and start managing growth. Let's build your
              assembly line.
            </p>
            <button className="btn-primary relative z-10" data-id="element-1400" data-mp="20vnd30qer7nm">Talk to MR →</button>
          </div>
        </div>
      </section>
    </main>;
}